import * as scmpAccount from "@product/scmp-account-sdk";
import { Modal } from "@product/scmp-sdk";
import { useSyncedRef } from "@react-hookz/web";
import type { FunctionComponent } from "react";
import { useCallback, useMemo } from "react";

import { useLoginDialogStateHelper, useScmpAccountEventHandlers } from "./hooks";
import CloseIcon from "./icon-close.svg";
import {
  CloseButton,
  Container,
  Disclaimer,
  LoginForm,
  StyledPostiesLogo,
  StyledScmpLogo,
} from "./styles";

export const LoginDialog: FunctionComponent = () => {
  const { closeLoginDialog, loginDialogState: state } = useLoginDialogStateHelper();
  const { attachEventHandlers, detachEventHandlers } = useScmpAccountEventHandlers();
  const latestDetachEventHandlers = useSyncedRef(detachEventHandlers);
  const onClose = () => closeLoginDialog();

  const reference = useCallback(
    (container: HTMLDivElement) => {
      if (!state.isVisible || !container) {
        latestDetachEventHandlers.current();
        return;
      }

      scmpAccount.init({
        APPLICATION: state.application,
        DESTINATION: state.destination,
      });
      scmpAccount.initLoginWidget(container.id, {
        destination: state.destination,
        emailLoginTitle: state.emailLoginTitle,
        loginDescription: state.description,
        registerSource: state.wallType === "regi" ? "loginwall" : "",
        title: state.title,
      });

      attachEventHandlers();
    },
    [
      attachEventHandlers,
      latestDetachEventHandlers,
      state.application,
      state.description,
      state.destination,
      state.emailLoginTitle,
      state.isVisible,
      state.title,
      state.wallType,
    ],
  );

  const renderLogo = useMemo(() => {
    switch (state.application) {
      case "posties":
        return <StyledPostiesLogo />;
      default:
        return <StyledScmpLogo />;
    }
  }, [state.application]);

  return (
    <Modal open={state.isVisible} disableAutoFocus onClose={onClose}>
      <Container>
        <CloseButton onClick={onClose}>
          <CloseIcon />
        </CloseButton>

        {renderLogo}

        <LoginForm>
          <div id="loginContainer" ref={reference} />
        </LoginForm>

        <Disclaimer>
          By creating an account, you agree to our <a href="/terms-conditions">Terms of Service</a>{" "}
          and acknowledge our <a href="/privacy-policy">Privacy Policy</a>.
        </Disclaimer>
      </Container>
    </Modal>
  );
};

LoginDialog.displayName = "LoginDialog";
